import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Modal from "react-bootstrap/Modal";
import jsonData from "../../VisaData/b2bvisas.json";
import VisaTabs from "../AllTabs/VisaTabs";

const localizer = momentLocalizer(moment);

const VisaAppointmentCalendar = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterVisa, setFilterVisa] = useState("All");
  const [filterDate, setFilterDate] = useState("");
  const [allVisas, setAllVisas] = useState([]);

  // Get all unique visas from JSON data
  const getAllVisas = () => {
    const visas = [];
    jsonData.forEach((country) => {
      country.visas.forEach((visa) => {
        visas.push({
          visaId: visa.visaId,
          visaName: visa.visaName,
          countryName: country.name,
          flagSrc: country.flagSrc,
          displayName: `${country.name} - ${visa.visaName}`,
        });
      });
    });
    return visas;
  };

  // Get visa details by ID
  const getVisaDetails = (visaId) => {
    for (const country of jsonData) {
      const visa = country.visas.find((v) => v.visaId === visaId);
      if (visa) {
        return {
          ...visa,
          countryName: country.name,
          flagSrc: country.flagSrc,
        };
      }
    }
    return null;
  };

  // Get all visa appointments
  const GetAllAppointments = async () => {
    try {
      const res = await apiService.getAll(config.GETALLVISAAPPOINTMENTS);
      const events = convertToCalendarEvents(res);
      setCalendarEvents(events);
    } catch (error) {
      console.error("Error fetching all visa appointments:", error);
    }
  };

  // Convert appointments to calendar events
  const convertToCalendarEvents = (appointments) => {
    const events = [];

    appointments.forEach((appointment) => {
      const visaDetails = getVisaDetails(appointment.visaId);
      const countryName = visaDetails?.countryName || "Unknown Country";
      const visaName = visaDetails?.visaName || "Unknown Visa";

      appointment.dates?.forEach((date) => {
        const eventDate = new Date(date.appointmentDate);
        events.push({
          id: `${appointment.visaAppointmentId}-${date.visaAppointmentDateId}`,
          title: `${countryName} - ${visaName}`,
          start: eventDate,
          end: eventDate,
          allDay: true,
          resource: {
            appointment,
            date,
            companyName: appointment.companyName,
            countryName,
            visaName,
            flagSrc: visaDetails?.flagSrc,
            price: date.price,
            slots: date.noOfSlots,
            status: date.status || "Available",
            visaId: appointment.visaId,
          },
        });
      });
    });

    return events;
  };

  // Get event style based on status
  const eventStyleGetter = (event) => {
    let backgroundColor = "#3174ad";

    switch (event.resource.status) {
      case "Available":
        backgroundColor = "#28a745";
        break;
      case "Sold Out":
        backgroundColor = "#dc3545";
        break;

      default:
        backgroundColor = "#6c757d";
    }

    return {
      style: {
        backgroundColor,
        borderRadius: "8px",
        opacity: 0.9,
        color: "white",
        border: "2px solid #fff",
        display: "block",
        fontSize: "11px",
        padding: "4px 6px",
        fontWeight: "bold",
        minHeight: "auto",
        margin: "1px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      },
    };
  };

  // Custom event component
  const CustomEvent = ({ event }) => (
    <div className="custom-event-content">
      <div className="event-title">{event.resource.companyName}</div>
      <div className="event-details">
        <small>
          {event.resource.countryName} - {event.resource.visaName}
        </small>
      </div>
      <div className="event-slots">
        <strong>{event.resource.slots} slots</strong>
      </div>
      <div className="event-status">
        <small>{event.resource.status}</small>
      </div>
    </div>
  );

  // Handle event click
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Filter events based on status, visa, and date
  const getFilteredEvents = () => {
    let filtered = calendarEvents;

    if (filterStatus !== "All") {
      filtered = filtered.filter(
        (event) => event.resource.status === filterStatus
      );
    }

    if (filterVisa !== "All") {
      filtered = filtered.filter(
        (event) => event.resource.visaId === filterVisa
      );
    }

    if (filterDate) {
      const selectedDate = moment(filterDate).format("YYYY-MM-DD");
      filtered = filtered.filter(
        (event) => moment(event.start).format("YYYY-MM-DD") === selectedDate
      );
    }

    return filtered;
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterStatus("All");
    setFilterVisa("All");
    setFilterDate("");
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  useEffect(() => {
    if (user && !loading) {
      GetAllAppointments();
      setAllVisas(getAllVisas());
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  const filteredEvents = getFilteredEvents();

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="p-2">
                  <VisaTabs pageName="APPOINTMENT CALENDAR" />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <div className="row g-3 align-items-center">
                      {/* Status Filter */}
                      <div className="col-md-3">
                        <label className="form-label">Filter by Status</label>
                        <select
                          className="form-select"
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                        >
                          <option value="All">All Status</option>
                          <option value="Available">Available</option>
                          <option value="Sold Out">Sold Out</option>
                        </select>
                      </div>

                      {/* Visa Filter */}
                      <div className="col-md-3">
                        <label className="form-label">Filter by Visa</label>
                        <select
                          className="form-select"
                          value={filterVisa}
                          onChange={(e) => setFilterVisa(e.target.value)}
                        >
                          <option value="All">All Visas</option>
                          {allVisas.map((visa) => (
                            <option key={visa.visaId} value={visa.visaId}>
                              {visa.displayName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Date Filter and Clear Button */}
                      <div className="col-md-4">
                        <div className="row g-2">
                          <div className="col-8">
                            <label className="form-label">Filter by Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterDate}
                              onChange={(e) => setFilterDate(e.target.value)}
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label d-block">&nbsp;</label>
                            <button
                              className="btn btn-danger w-100"
                              onClick={clearFilters}
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Total Appointments Only */}
                      <div className="col-md-2">
                        <div className="text-center border rounded p-3 bg-light h-100">
                          <div className="fw-bold text-primary fs-3">
                            {filteredEvents.length}
                          </div>
                          <div className="small text-muted">
                            Total Appointments
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div style={{ height: "70vh" }}>
                      <Calendar
                        localizer={localizer}
                        events={filteredEvents}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: "100%" }}
                        eventPropGetter={eventStyleGetter}
                        components={{
                          event: CustomEvent,
                        }}
                        views={["month", "week", "day", "agenda"]}
                        defaultView="month"
                        popup
                        onSelectEvent={handleSelectEvent}
                        onSelectSlot={(slotInfo) => {
                          if (slotInfo.action === "click") {
                            setFilterDate(
                              moment(slotInfo.start).format("YYYY-MM-DD")
                            );
                          }
                        }}
                        selectable
                        messages={{
                          next: "Next",
                          previous: "Prev",
                          today: "Today",
                          month: "Month",
                          week: "Week",
                          day: "Day",
                          agenda: "Agenda",
                          date: "Date",
                          time: "Time",
                          event: "Event",
                          noEventsInRange:
                            "No appointments found for the selected filters",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Event Details Modal */}
        <Modal show={showEventModal} onHide={handleCloseEventModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Appointment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedEvent && (
              <div className="row">
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100">
                    <h6 className="border-bottom pb-2">Supplier Information</h6>
                    <div className="d-flex align-items-center mb-3">
                      {selectedEvent.resource.flagSrc && (
                        <img
                          src={`https://flagcdn.com/w40/${selectedEvent.resource.flagSrc.toLowerCase()}.png`}
                          alt={selectedEvent.resource.countryName}
                          className="me-3"
                          style={{
                            width: "40px",
                            height: "30px",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      <div>
                        <h5 className="mb-1">
                          {selectedEvent.resource.countryName}
                        </h5>
                        <p className="text-muted mb-0">
                          {selectedEvent.resource.visaName} Visa
                        </p>
                      </div>
                    </div>
                    <table className="table table-sm table-borderless">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: "40%" }}>
                            Company:
                          </td>
                          <td>{selectedEvent.resource.companyName}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Status:</td>
                          <td>
                            <span
                              className={`badge bg-${getStatusBadge(
                                selectedEvent.resource.status
                              )}`}
                            >
                              {selectedEvent.resource.status}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100">
                    <h6 className="border-bottom pb-2">Appointment Details</h6>
                    <div className="text-center mb-3">
                      <div className="display-6 text-primary">
                        {moment(selectedEvent.start).format("DD")}
                      </div>
                      <div className="text-muted">
                        {moment(selectedEvent.start).format("MMMM YYYY")}
                      </div>
                      <div className="fw-bold">
                        {moment(selectedEvent.start).format("dddd")}
                      </div>
                    </div>
                    <table className="table table-sm table-borderless">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: "40%" }}>
                            Available Slots:
                          </td>
                          <td>
                            <span className="badge bg-primary fs-6">
                              {selectedEvent.resource.slots}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Price:</td>
                          <td>
                            <span className="fw-bold text-success fs-5">
                              ₹{selectedEvent.resource.price}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Date:</td>
                          <td>
                            {moment(selectedEvent.start).format(
                              "MMMM Do, YYYY"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-danger" onClick={handleCloseEventModal}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </div>

      <style jsx>{`
        .custom-event-content {
          padding: 2px;
          line-height: 1.2;
        }
        .event-title {
          font-weight: bold;
          font-size: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .event-details {
          font-size: 9px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .event-slots {
          font-size: 9px;
          margin-top: 1px;
        }
        .event-status {
          font-size: 8px;
          margin-top: 1px;
          opacity: 0.9;
        }
        .rbc-event {
          min-height: 50px !important;
        }
      `}</style>
    </div>
  );
};

// Helper function for status badges
const getStatusBadge = (status) => {
  switch (status) {
    case "Available":
      return "success";
    case "Sold out":
      return "danger";

    default:
      return "secondary";
  }
};

export default VisaAppointmentCalendar;
